import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, ListChecks, Clock } from 'lucide-react';
import HelpPanel, { HelpSection, HelpCode } from '../components/HelpPanel';
import { fetchSyncLogs } from '../api/integration.api';
import type { SyncLogEntry, PaginatedResponse } from '../types/integration.types';
import { fmtDate, fmtTime } from '@/lib/format-date';

const METHOD_COLORS: Record<string, string> = {
  GET: '#3b82f6',
  POST: '#22c55e',
  PATCH: '#f59e0b',
  DELETE: '#ef4444',
};

export default function SyncLogPage() {
  const [data, setData] = useState<PaginatedResponse<SyncLogEntry> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [onlyErrors, setOnlyErrors] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchSyncLogs({ page, limit: 50, onlyErrors });
      setData(result);
      setError('');
    } catch {
      setError('Fehler beim Laden des Sync-Protokolls');
    } finally {
      setLoading(false);
    }
  }, [page, onlyErrors]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Sync-Protokoll</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Hier siehst du alle API-Zugriffe deiner anderen Programme.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={onlyErrors}
              onChange={(e) => { setOnlyErrors(e.target.checked); setPage(1); }}
              className="rounded"
            />
            Nur Fehler
          </label>
          <button
            onClick={load}
            className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted text-foreground"
          >
            <RefreshCw size={14} />
            Aktualisieren
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ── Hilfe-Panel ──────────────────────────────────────────────────── */}
      <HelpPanel title="Protokoll verstehen">
        <HelpSection icon={<ListChecks size={15} />} title="Status-Codes">
          <ul className="space-y-1 list-none pl-0">
            <li><HelpCode>200</HelpCode> / <HelpCode>201</HelpCode> — Erfolg (Daten gelesen bzw. synchronisiert)</li>
            <li><HelpCode>400</HelpCode> — Ungültiges Request-Format (z.B. fehlende Pflichtfelder)</li>
            <li><HelpCode>401</HelpCode> — API-Key fehlt, ist ungültig oder abgelaufen</li>
            <li><HelpCode>403</HelpCode> — Der API-Key hat nicht den erforderlichen Scope für diesen Endpunkt</li>
            <li><HelpCode>409</HelpCode> — Modus-Konflikt: Schreibversuch auf einen Bereich, der auf „Intern" steht</li>
            <li><HelpCode>422</HelpCode> — Validierungsfehler im Payload (z.B. Custom-Field entspricht nicht dem Schema)</li>
            <li><HelpCode>429</HelpCode> — Rate-Limit überschritten (max. 1.000 Requests/Stunde pro Key)</li>
          </ul>
        </HelpSection>

        <HelpSection icon={<Clock size={15} />} title="Dauer und Datensätze">
          <p>
            Die Spalte <strong>Dauer</strong> zeigt die Antwortzeit in Millisekunden.
            Werte über 2.000 ms können auf sehr große Payloads oder hohe Serverlast hindeuten.
          </p>
          <p className="mt-1.5">
            Die Spalte <strong>Datensätze</strong> zeigt, wie viele Einträge gelesen
            oder synchronisiert wurden. Bei Fehlern (4xx/5xx) ist dieser Wert leer.
          </p>
        </HelpSection>
      </HelpPanel>

      {loading ? (
        <p className="text-muted-foreground text-sm">Lade Protokoll...</p>
      ) : !data || data.data.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          <p className="font-medium">Noch keine API-Zugriffe protokolliert</p>
          <p className="text-sm mt-1">
            Sobald dein anderes Programm die API nutzt, erscheinen die Einträge hier.
          </p>
        </div>
      ) : (
        <>
          {/* Tabelle */}
          <div className="rounded-lg border overflow-hidden" style={{ background: 'hsl(var(--card))' }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Zeitpunkt</th>
                  <th className="px-4 py-3 font-medium">Key</th>
                  <th className="px-4 py-3 font-medium">Methode</th>
                  <th className="px-4 py-3 font-medium">Endpunkt</th>
                  <th className="px-4 py-3 font-medium text-right">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Datensätze</th>
                  <th className="px-4 py-3 font-medium text-right">Dauer</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((entry) => (
                  <tr key={entry.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2.5 text-foreground whitespace-nowrap">
                      {fmtDate(entry.createdAt)} {fmtTime(entry.createdAt)}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {entry.apiKeyName ?? '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className="rounded px-1.5 py-0.5 text-xs font-bold text-white"
                        style={{ backgroundColor: METHOD_COLORS[entry.method] || '#6b7280' }}
                      >
                        {entry.method}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-foreground max-w-xs truncate">
                      {entry.endpoint}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {entry.statusCode < 400 ? (
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <CheckCircle2 size={14} />
                          {entry.statusCode}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-500" title={entry.errorMessage ?? ''}>
                          <AlertCircle size={14} />
                          {entry.statusCode}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground">
                      {entry.recordCount ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground whitespace-nowrap">
                      {entry.durationMs != null ? `${entry.durationMs} ms` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.meta.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
              <span>
                Seite {data.meta.page} von {data.meta.totalPages} ({data.meta.total} Einträge)
              </span>
              <div className="flex gap-1">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="rounded-md border px-2 py-1 hover:bg-muted disabled:opacity-40"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  disabled={page >= data.meta.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-md border px-2 py-1 hover:bg-muted disabled:opacity-40"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
